
import React, { useMemo } from 'react';
import { Bucket, BucketTheme } from '../types';
import { CheckCircle2, Circle, Trophy, PieChart, Activity, TrendingUp, Target, Flag, Calendar, Crown, BarChart3, Plus } from 'lucide-react';
import { useLanguage } from '../i18n';

interface StatisticsViewProps {
  buckets: Bucket[];
  onCreateBucket?: () => void;
}

const THEME_COLORS: Record<BucketTheme, { text: string; bg: string; bar: string }> = {
  [BucketTheme.Blue]: { text: 'text-blue-400', bg: 'bg-blue-500/20', bar: 'bg-blue-500' },
  [BucketTheme.Purple]: { text: 'text-purple-400', bg: 'bg-purple-500/20', bar: 'bg-purple-500' },
  [BucketTheme.Green]: { text: 'text-emerald-400', bg: 'bg-emerald-500/20', bar: 'bg-emerald-500' },
  [BucketTheme.Orange]: { text: 'text-orange-400', bg: 'bg-orange-500/20', bar: 'bg-orange-500' },
  [BucketTheme.Pink]: { text: 'text-pink-400', bg: 'bg-pink-500/20', bar: 'bg-pink-500' },
  [BucketTheme.Red]: { text: 'text-red-400', bg: 'bg-red-500/20', bar: 'bg-red-500' },
  [BucketTheme.Teal]: { text: 'text-teal-400', bg: 'bg-teal-500/20', bar: 'bg-teal-500' },
  [BucketTheme.Yellow]: { text: 'text-yellow-400', bg: 'bg-yellow-500/20', bar: 'bg-yellow-500' },
  [BucketTheme.Indigo]: { text: 'text-indigo-400', bg: 'bg-indigo-500/20', bar: 'bg-indigo-500' },
  [BucketTheme.Cyan]: { text: 'text-cyan-400', bg: 'bg-cyan-500/20', bar: 'bg-cyan-500' },
  [BucketTheme.Lime]: { text: 'text-lime-400', bg: 'bg-lime-500/20', bar: 'bg-lime-500' },
  [BucketTheme.Rose]: { text: 'text-rose-400', bg: 'bg-rose-500/20', bar: 'bg-rose-500' },
};

export const StatisticsView: React.FC<StatisticsViewProps> = ({ buckets, onCreateBucket }) => {
  const { t } = useLanguage();

  const stats = useMemo(() => {
    let totalTasks = 0;
    let completedTasks = 0;
    let totalPoints = 0;
    let earnedPoints = 0;
    let totalLevels = 0;

    const difficultyCounts = { easy: 0, medium: 0, hard: 0 };
    const timeCounts = { short: 0, medium: 0, long: 0 };

    const externalContributions = new Map<string, any[]>();
    buckets.forEach(b => {
        b.todos.forEach(t => {
            if (t.contributesTo) {
                t.contributesTo.forEach(targetId => {
                    if (!externalContributions.has(targetId)) externalContributions.set(targetId, []);
                    externalContributions.get(targetId)?.push(t);
                });
            }
        });
    });

    const bucketStats = buckets.map(b => {
      const external = externalContributions.get(b.id) || [];
      const allTodos = [...b.todos, ...external];

      const bTotal = allTodos.length;
      const bCompleted = allTodos.filter(t => t.completed).length;
      const bPointsTotal = allTodos.reduce((sum, t) => sum + (t.points || 1), 0);
      const bPointsEarned = allTodos.reduce((sum, t) => sum + (t.completed ? (t.points || 1) : 0) + (t.accumulatedPoints || 0), 0);
      
      const LEVEL_STEP = 20;
      const currentLevel = Math.floor(bPointsEarned / LEVEL_STEP) + 1;
      const levelPoints = bPointsEarned % LEVEL_STEP;
      const levelPercent = Math.min(100, Math.round((levelPoints / LEVEL_STEP) * 100));

      totalTasks += bTotal;
      completedTasks += bCompleted;
      totalPoints += bPointsTotal;
      earnedPoints += bPointsEarned;
      totalLevels += currentLevel;

      b.todos.forEach(t => {
          if (t.difficulty) difficultyCounts[t.difficulty]++;
          else {
              if (t.points <= 2) difficultyCounts.easy++;
              else if (t.points <= 4) difficultyCounts.medium++;
              else difficultyCounts.hard++;
          }
          if (t.timeConsumption) timeCounts[t.timeConsumption]++;
          else timeCounts.medium++;
      });

      return {
        ...b,
        stats: {
          total: bTotal,
          completed: bCompleted,
          pointsTotal: bPointsTotal,
          pointsEarned: bPointsEarned,
          currentLevel,
          levelPoints,
          levelPercent
        }
      };
    });

    const uniqueTotalTasks = buckets.reduce((acc, b) => acc + b.todos.length, 0);
    const uniqueCompletedTasks = buckets.reduce((acc, b) => acc + b.todos.filter(t => t.completed).length, 0);

    return {
      totalTasks: uniqueTotalTasks,
      completedTasks: uniqueCompletedTasks,
      openTasks: uniqueTotalTasks - uniqueCompletedTasks,
      totalPoints,
      earnedPoints,
      totalLevels,
      bucketStats,
      difficultyCounts,
      timeCounts,
      completionRate: uniqueTotalTasks === 0 ? 0 : Math.round((uniqueCompletedTasks / uniqueTotalTasks) * 100)
    };
  }, [buckets]);

  if (buckets.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-24 text-center animate-in fade-in duration-700">
            <div className="w-24 h-24 bg-indigo-500/10 rounded-full flex items-center justify-center mb-6 relative">
                 <div className="absolute inset-0 bg-indigo-500/20 blur-2xl rounded-full"></div>
                 <BarChart3 size={48} className="text-indigo-400 relative z-10" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-3">{t.statsOverview}</h2>
            <p className="text-slate-500 max-w-md mx-auto mb-8">{t.completeTasksToSee}</p>
            {onCreateBucket && (
                <button 
                    onClick={onCreateBucket}
                    className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold transition-all shadow-xl shadow-indigo-600/20 active:scale-95"
                >
                    <Plus size={20} />
                    {t.createFirstBucket}
                </button>
            )}
        </div>
      );
  }

  const activeBuckets = stats.bucketStats.filter(b => b.stats.pointsEarned > 0);
  const totalEarnedForCalc = activeBuckets.reduce((sum, b) => sum + b.stats.pointsEarned, 0);
  const bucketsWithHistory = buckets.filter(b => b.milestones && b.milestones.length > 0);

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-10">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-slate-900/50 border border-white/5 rounded-2xl p-6 backdrop-blur-sm flex items-center gap-4 hover:bg-slate-800/50 transition-colors">
            <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-slate-400">
                <Circle size={24} />
            </div>
            <div>
                <p className="text-sm text-slate-500 font-medium uppercase tracking-wider">{t.openTasks}</p>
                <p className="text-3xl font-bold text-white">{stats.openTasks}</p>
            </div>
        </div>
        <div className="bg-slate-900/50 border border-white/5 rounded-2xl p-6 backdrop-blur-sm flex items-center gap-4 hover:bg-slate-800/50 transition-colors">
            <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                <CheckCircle2 size={24} />
            </div>
            <div>
                <p className="text-sm text-emerald-500/80 font-medium uppercase tracking-wider">{t.doneTasks}</p>
                <p className="text-3xl font-bold text-white">{stats.completedTasks}</p>
            </div>
        </div>
        <div className="bg-slate-900/50 border border-white/5 rounded-2xl p-6 backdrop-blur-sm flex items-center gap-4 hover:bg-slate-800/50 transition-colors">
            <div className="w-12 h-12 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                <Crown size={24} />
            </div>
            <div>
                <p className="text-sm text-indigo-400/80 font-medium uppercase tracking-wider">{t.totalLevels}</p>
                <p className="text-3xl font-bold text-white">{stats.totalLevels}</p>
            </div>
        </div>
        <div className="bg-slate-900/50 border border-white/5 rounded-2xl p-6 backdrop-blur-sm flex items-center gap-4 hover:bg-slate-800/50 transition-colors">
            <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-400">
                <Trophy size={24} />
            </div>
            <div>
                <p className="text-sm text-yellow-500/80 font-medium uppercase tracking-wider">{t.pointsEarned}</p>
                <p className="text-3xl font-bold text-white">{stats.earnedPoints}</p>
            </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
         <div className="bg-slate-900/50 border border-white/5 rounded-3xl p-8 backdrop-blur-sm">
            <h3 className="text-xl font-bold mb-6 flex items-center gap-3 text-white">
                <Activity className="text-indigo-400" size={24} /> 
                {t.bucketProgress}
                <span className="text-xs font-normal text-slate-500 ml-auto bg-white/5 px-2 py-1 rounded-full">{t.basedOnPoints}</span>
            </h3>
            <div className="space-y-6">
                {stats.bucketStats.map(bucket => {
                    const colors = THEME_COLORS[bucket.theme];
                    const levelTitle = t.levelTitles[Math.min(bucket.stats.currentLevel - 1, t.levelTitles.length - 1)];
                    return (
                        <div key={bucket.id}>
                            <div className="flex justify-between items-end mb-2">
                                <span className={`font-medium ${colors.text} flex items-center gap-2`}>{bucket.name}</span>
                                <div className="text-right flex items-center gap-2">
                                    <div className="relative overflow-hidden flex items-center gap-1 bg-slate-950 px-2 py-0.5 rounded text-[10px] uppercase font-black text-amber-400 border border-white/5">
                                        <div className="absolute inset-y-0 left-0 bg-indigo-500/20 border-r border-indigo-400/30 transition-all duration-1000 z-0" style={{ width: `${bucket.stats.levelPercent}%` }}></div>
                                        <Crown size={10} className="relative z-10" />
                                        <span className="relative z-10">LVL {bucket.stats.currentLevel}</span>
                                    </div>
                                    <span className="text-xs text-slate-500 font-medium">{levelTitle}</span>
                                </div>
                            </div>
                            <div className="h-3 w-full bg-black/40 rounded-full overflow-hidden border border-white/5 relative">
                                <div className={`h-full ${colors.bar} transition-all duration-1000 ease-out`} style={{ width: `${bucket.stats.levelPercent}%` }} />
                            </div>
                            <div className="flex justify-between mt-1 text-[10px] text-slate-600 uppercase tracking-widest font-medium">
                                <span>{bucket.stats.levelPoints} / 20 {t.pts} to next lvl</span>
                                <span>Total: {bucket.stats.pointsEarned} {t.pts}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
         </div>

         <div className="space-y-8">
            <div className="bg-slate-900/50 border border-white/5 rounded-3xl p-8 backdrop-blur-sm flex flex-col">
                <h3 className="text-xl font-bold mb-6 flex items-center gap-3 text-white">
                    <PieChart className="text-purple-400" size={24} /> 
                    {t.focusDistribution}
                </h3>
                <div className="flex-1 flex flex-col justify-center">
                    {totalEarnedForCalc > 0 ? (
                        <>
                            <div className="flex w-full h-8 rounded-full overflow-hidden mb-8 border border-white/5">
                                {activeBuckets.map(bucket => {
                                    const colors = THEME_COLORS[bucket.theme];
                                    const share = (bucket.stats.pointsEarned / totalEarnedForCalc) * 100;
                                    return (
                                        <div key={bucket.id} className={`${colors.bar} hover:opacity-80 transition-opacity`} style={{ width: `${share}%` }} title={`${bucket.name}: ${Math.round(share)}%`} />
                                    );
                                })}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                {activeBuckets.map(bucket => {
                                    const colors = THEME_COLORS[bucket.theme];
                                    const share = (bucket.stats.pointsEarned / totalEarnedForCalc) * 100;
                                    return (
                                        <div key={bucket.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5">
                                            <div className={`w-3 h-3 rounded-full ${colors.bar} flex-shrink-0`} />
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-slate-200 truncate">{bucket.name}</p>
                                                <p className="text-xs text-slate-500">{t.totalEffort(share)}</p>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </>
                    ) : (
                        <div className="flex flex-col items-center justify-center text-slate-500 h-40">
                            <Target size={48} className="mb-4 opacity-20" />
                            <p>{t.completeTasksToSee}</p>
                        </div>
                    )}
                </div>
            </div>

            <div className="bg-slate-900/50 border border-white/5 rounded-3xl p-8 backdrop-blur-sm">
                <h3 className="text-xl font-bold mb-6 flex items-center gap-3 text-white">
                    <BarChart3 className="text-teal-400" size={24} /> 
                    {t.complexityAnalysis}
                </h3>
                <div className="grid grid-cols-2 gap-8">
                    <div>
                        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">{t.difficultyDist}</h4>
                        <div className="space-y-3">
                            <div className="space-y-1">
                                <div className="flex justify-between text-[10px] text-slate-400"><span>{t.diffEasy}</span><span>{stats.difficultyCounts.easy}</span></div>
                                <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-green-500" style={{ width: `${stats.totalTasks ? (stats.difficultyCounts.easy / stats.totalTasks) * 100 : 0}%` }}></div></div>
                            </div>
                            <div className="space-y-1">
                                <div className="flex justify-between text-[10px] text-slate-400"><span>{t.diffMedium}</span><span>{stats.difficultyCounts.medium}</span></div>
                                <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-yellow-500" style={{ width: `${stats.totalTasks ? (stats.difficultyCounts.medium / stats.totalTasks) * 100 : 0}%` }}></div></div>
                            </div>
                            <div className="space-y-1">
                                <div className="flex justify-between text-[10px] text-slate-400"><span>{t.diffHard}</span><span>{stats.difficultyCounts.hard}</span></div>
                                <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-red-500" style={{ width: `${stats.totalTasks ? (stats.difficultyCounts.hard / stats.totalTasks) * 100 : 0}%` }}></div></div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">{t.timeDist}</h4>
                        <div className="space-y-3">
                            <div className="space-y-1">
                                <div className="flex justify-between text-[10px] text-slate-400"><span>{t.timeShort}</span><span>{stats.timeCounts.short}</span></div>
                                <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-green-400" style={{ width: `${stats.totalTasks ? (stats.timeCounts.short / stats.totalTasks) * 100 : 0}%` }}></div></div>
                            </div>
                            <div className="space-y-1">
                                <div className="flex justify-between text-[10px] text-slate-400"><span>{t.timeMedium}</span><span>{stats.timeCounts.medium}</span></div>
                                <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-yellow-400" style={{ width: `${stats.totalTasks ? (stats.timeCounts.medium / stats.totalTasks) * 100 : 0}%` }}></div></div>
                            </div>
                            <div className="space-y-1">
                                <div className="flex justify-between text-[10px] text-slate-400"><span>{t.timeLong}</span><span>{stats.timeCounts.long}</span></div>
                                <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-red-400" style={{ width: `${stats.totalTasks ? (stats.timeCounts.long / stats.totalTasks) * 100 : 0}%` }}></div></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
         </div>
      </div>

      <div className="bg-slate-900/50 border border-white/5 rounded-3xl p-8 backdrop-blur-sm">
          <h3 className="text-xl font-bold mb-6 flex items-center gap-3 text-white">
              <Flag className="text-yellow-400" size={24} /> 
              {t.milestoneHistory}
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {bucketsWithHistory.length > 0 ? (
                  bucketsWithHistory.map(bucket => {
                      const colors = THEME_COLORS[bucket.theme];
                      return (
                          <div key={bucket.id} className="bg-white/5 rounded-2xl p-5 border border-white/5">
                               <div className="flex items-center gap-3 mb-4">
                                   <div className={`w-8 h-8 rounded-full ${colors.bg} ${colors.text} flex items-center justify-center`}><Trophy size={16} /></div>
                                   <h4 className={`font-semibold ${colors.text}`}>{bucket.name}</h4>
                               </div>
                               <div className="space-y-3">
                                   {bucket.milestones!.map((milestone) => (
                                       <div key={milestone.id} className="flex items-center justify-between p-3 rounded-xl bg-slate-900/50 border border-white/5">
                                            <div>
                                                <p className="text-sm font-medium text-white">{milestone.title}</p>
                                                <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5"><Calendar size={10} />{t.reachedOn} {new Date(milestone.reachedAt).toLocaleDateString()}</p>
                                            </div>
                                            <div className="bg-yellow-500/10 text-yellow-500 border border-yellow-500/20 px-2 py-1 rounded text-xs font-bold whitespace-nowrap">{milestone.targetPoints} {t.pts}</div>
                                       </div>
                                   )).reverse()}
                               </div>
                          </div>
                      );
                  })
              ) : (
                  <div className="col-span-full text-center text-slate-500 py-12 border-2 border-dashed border-white/5 rounded-xl">
                      <Trophy size={32} className="mx-auto mb-3 opacity-20" />
                      <p>{t.noMilestones}</p>
                  </div>
              )}
          </div>
      </div>
    </div>
  );
};
